import React from 'react';

import Heading from '@atlaskit/heading/heading';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import { useModal } from '@atlaskit/modal-dialog/hooks';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import SearchInput from './input';

const headerLayoutStyles = xcss({
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	gap: 'space.200',
	borderBlockEndWidth: 'border.width',
	borderBlockEndStyle: 'solid',
	borderBlockEndColor: 'color.border',
	paddingBlock: 'space.150',
	paddingInline: 'space.200',
});

const headerInputWrapperStyles = xcss({
	flex: '1 1 auto',
});

const customHeaderStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexDirection: 'row-reverse',
});

export type SearchModalHeaderProps = {
	testId?: string;
	onQueryChange(value: string): void;
	onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
	inputRef: React.RefObject<HTMLInputElement>;
	isExpanded: boolean;
	listboxId: string;
	activeDescendantId?: string;
	onRefineReady?: (refine: (query: string) => void) => void;
};

/**
 * __Search modal header__
 *
 * Header for the search modal, including the search input and close button.
 *
 */
export const SearchModalHeader = ({
	testId,
	onQueryChange,
	onKeyDown,
	inputRef,
	isExpanded,
	listboxId,
	activeDescendantId,
	onRefineReady,
}: SearchModalHeaderProps): React.JSX.Element => {
	const { titleId, onClose } = useModal();

	if (fg('platform_dst_modal-dialog-use-modal-title')) {
		return (
			<ModalHeader hasCloseButton>
				<Box xcss={headerInputWrapperStyles}>
					<VisuallyHidden>
						<ModalTitle>Search</ModalTitle>
					</VisuallyHidden>
					<SearchInput
						testId={testId}
						onQueryChange={onQueryChange}
						onKeyDown={onKeyDown}
						inputRef={inputRef}
						isExpanded={isExpanded}
						listboxId={listboxId}
						activeDescendantId={activeDescendantId}
						onRefineReady={onRefineReady}
					/>
				</Box>
			</ModalHeader>
		);
	}

	return (
		<Box xcss={[customHeaderStyles, headerLayoutStyles]} padding="space.300">
			{/* Close button first in the DOM for accessibility; layout reversed visually */}
			<CloseButton onClick={onClose} />
			<Box xcss={headerInputWrapperStyles}>
				<VisuallyHidden>
					<Heading as="h1" size="medium" id={titleId}>
						Search
					</Heading>
				</VisuallyHidden>
				<SearchInput
					testId={testId}
					onQueryChange={onQueryChange}
					onKeyDown={onKeyDown}
					inputRef={inputRef}
					isExpanded={isExpanded}
					listboxId={listboxId}
					activeDescendantId={activeDescendantId}
					onRefineReady={onRefineReady}
				/>
			</Box>
		</Box>
	);
};
